import {
  advancedDimensionDefinitions,
  bookingPolicyOptions,
  coreDimensionDefinitions,
  costLevelLabels,
  durationOptions,
  mobilityLimitOptions,
  neighbourhoodCharacterDefinitions,
  presets,
  priceOptions,
  seasonDefinitions,
  timeOfDayDefinitions,
  transportModeDefinitions,
  travellerDefinitions,
  travelTimeOptions,
  waterRelationshipDefinitions,
} from "./filter-config.js";
import {
  formatMinutes,
  formatSek,
  getComparablePrice,
  getDimensionScore,
} from "./filter-engine.js";
import { icon } from "./icons.js";
import type {
  ChoiceDefinition,
} from "./filter-config.js";
import type {
  AdvanceBooking,
  CostLevel,
  DimensionKey,
  FilterChip,
  FilterState,
  MatchReason,
  RankedAttraction,
  RelaxSuggestion,
  TravelContentEntry,
} from "./types.js";
import { fallbackImage, getAttractionVisual } from "./visuals.js";

const presetStyles = {
  sun: {
    card: "border-amber-200 bg-amber-50/90 hover:border-amber-300",
    icon: "bg-amber-100 text-amber-900",
    iconName: "sun" as const,
  },
  rain: {
    card: "border-sky-200 bg-sky-50/80 hover:border-sky-300",
    icon: "bg-sky-100 text-sky-800",
    iconName: "cloudRain" as const,
  },
  walk: {
    card: "border-amber-200 bg-amber-50/80 hover:border-amber-300",
    icon: "bg-amber-100 text-amber-900",
    iconName: "walking" as const,
  },
  family: {
    card: "border-rose-200 bg-rose-50/80 hover:border-rose-300",
    icon: "bg-rose-100 text-rose-800",
    iconName: "people" as const,
  },
  budget: {
    card: "border-emerald-200 bg-emerald-50/80 hover:border-emerald-300",
    icon: "bg-emerald-100 text-emerald-800",
    iconName: "wallet" as const,
  },
  water: {
    card: "border-cyan-200 bg-cyan-50/80 hover:border-cyan-300",
    icon: "bg-cyan-100 text-cyan-900",
    iconName: "water" as const,
  },
};

const primaryDimensionKeys = new Set<DimensionKey>([
  "sunny",
  "rain",
  "history",
  "atmosphere",
  "walking",
  "water",
  "localness",
  "children",
  "value",
]);

const primaryDimensions = coreDimensionDefinitions.filter((definition) => primaryDimensionKeys.has(definition.key));
const secondaryDimensions = [
  ...coreDimensionDefinitions.filter((definition) => !primaryDimensionKeys.has(definition.key)),
  ...advancedDimensionDefinitions,
];

export function renderApp(
  state: FilterState,
  ranked: RankedAttraction[],
  totalAttractions: number,
  chips: FilterChip[],
  relaxSuggestions: RelaxSuggestion[],
): string {
  return `
    <main class="min-h-screen">
      <section id="finder" class="pb-16 pt-3 sm:pt-5">
        <div class="mx-auto max-w-7xl px-3 sm:px-5 lg:px-8">
          <div class="rounded-[1.75rem] border border-white/80 bg-white/95 p-3 shadow-xl shadow-teal-950/8 backdrop-blur sm:p-5 lg:p-6">
            ${renderFinderIntro(state)}
            ${renderPresets(state)}

            <div class="mt-3 border-t border-slate-200 pt-3 sm:mt-4 sm:pt-4">
              <div class="grid gap-4 lg:grid-cols-[21rem_minmax(0,1fr)] xl:grid-cols-[23rem_minmax(0,1fr)]">
                <aside class="hidden lg:block" aria-label="Filter">
                  <div class="sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto rounded-2xl border border-slate-200 bg-sand-50/80 p-3 shadow-sm">
                    ${renderFilterPanel(state, "desktop")}
                  </div>
                </aside>
                <div class="min-w-0">
                  <div id="results-region">
                    ${renderResults(state, ranked, totalAttractions, chips, relaxSuggestions)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
    ${renderFooter()}
    ${renderMobileFilterDialog(state, chips.length)}
    <dialog id="detail-dialog" class="detail-dialog m-auto w-[min(94vw,62rem)] rounded-3xl bg-white p-0 text-slate-800 shadow-2xl backdrop:bg-teal-950/70 backdrop:backdrop-blur-sm">
      <div id="detail-dialog-content"></div>
    </dialog>
  `;
}

function renderFinderIntro(state: FilterState): string {
  return `
    <div class="flex items-center gap-2.5">
      <a href="/" class="group inline-flex shrink-0 items-center gap-2.5 rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700" aria-label="Stockholm passt zu mir – Startseite">
        <span class="grid size-10 place-items-center rounded-xl bg-teal-950 text-white shadow-sm transition group-hover:-rotate-3">${icon("compass", "size-5")}</span>
        <span class="hidden leading-tight md:block">
          <span class="block text-[0.7rem] font-black uppercase tracking-[0.16em] text-slate-950">Stockholm</span>
          <span class="block text-[0.68rem] font-semibold text-teal-700">passt zu mir</span>
        </span>
      </a>
      <h1 class="sr-only">Stockholm-Erlebnisse finden, die zu dir passen</h1>
      <label class="group min-w-0 flex-1">
        <span class="sr-only">Erlebnisse durchsuchen</span>
        <span class="relative block">
          <span class="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-slate-400 group-focus-within:text-teal-700">${icon("search", "size-[1.125rem]")}</span>
          <input data-filter="query" type="search" value="${escapeAttribute(state.query)}" placeholder="Altstadt, Sonne, Fika …" class="h-10 w-full rounded-full border border-slate-300 bg-white pl-10 pr-3 text-sm font-semibold text-slate-950 shadow-sm outline-none transition placeholder:font-normal placeholder:text-slate-400 focus:border-teal-600 focus:ring-4 focus:ring-teal-100 sm:h-11 sm:text-base" />
        </span>
      </label>
    </div>
  `;
}

function renderPresets(state: FilterState): string {
  return `
    <nav class="preset-strip mt-2.5 flex gap-2 overflow-x-auto pb-1" aria-label="Schnellstart">
        ${presets.map((preset) => {
          const style = presetStyles[preset.accent];
          const active = state.activePreset === preset.id;
          return `
            <button type="button" data-preset="${preset.id}" aria-pressed="${active}" aria-label="${escapeAttribute(`${preset.eyebrow}: ${preset.title}. ${preset.description}`)}" title="${escapeAttribute(preset.description)}" class="group inline-flex h-10 shrink-0 items-center gap-2 rounded-full border px-2.5 pr-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700 ${style.card} ${active ? "ring-2 ring-teal-700 ring-offset-1" : ""}">
              <span class="grid size-7 place-items-center rounded-full ${style.icon}">${icon(style.iconName, "size-4")}</span>
              <span class="whitespace-nowrap text-xs font-black text-slate-900">${escapeHtml(preset.title)}</span>
            </button>
          `;
        }).join("")}
    </nav>
  `;
}

export function renderFilterPanel(state: FilterState, prefix: string): string {
  const primaryCount = primaryDimensions.filter((definition) => state.minScores[definition.key] > 0).length;
  const secondaryCount = secondaryDimensions.filter((definition) => state.minScores[definition.key] > 0).length;
  const travellerCount = state.travellerTypes.length;
  const budgetCount = [state.maxCostLevel !== "any", state.maxPriceSek !== null, state.maxDurationMinutes !== null, state.maxTravelTimeMinutes !== null].filter(Boolean).length;
  const weatherCount = [state.minIndoorPercentage > 0, state.rainSafeOnly, state.seasons.length > 0, state.timesOfDay.length > 0].filter(Boolean).length;
  const planningCount = [state.bookingPolicy !== "any", state.maxMobility !== "any", state.childrenOnly, state.childAge !== null, state.carFreeOnly, state.quietOnly, state.toiletsOnly, state.minStrollerScore > 0, state.maxPhysicalEffort !== null].filter(Boolean).length;
  const placeCount = state.transportModes.length + state.neighbourhoodCharacters.length + state.waterRelationships.length;

  return `
    <div class="space-y-2" data-filter-panel="${prefix}">
      ${renderFilterAccordion(
        "primary",
        "Wichtigste Kriterien",
        "Mindestwerte werden gemeinsam angewendet.",
        primaryCount,
        true,
        `<div class="space-y-3.5">${primaryDimensions.map((definition) => renderDimensionControl(definition, state, prefix)).join("")}</div>`,
      )}

      ${renderFilterAccordion(
        "secondary",
        "Weitere Erlebniswerte",
        "Kultur, Kälte, Natur, Romantik und mehr.",
        secondaryCount,
        false,
        `<div class="space-y-3.5">${secondaryDimensions.map((definition) => renderDimensionControl(definition, state, prefix)).join("")}</div>`,
      )}

      ${renderFilterAccordion(
        "traveller",
        "Für wen reist du?",
        "Jedes gewählte Profil muss mindestens gut passen.",
        travellerCount,
        false,
        renderChoiceChips(`${prefix}-traveller`, "traveller", travellerDefinitions, state.travellerTypes),
      )}

      ${renderFilterAccordion(
        "budget",
        "Budget & Zeit",
        "Preisniveau, Demo-Preis, Dauer und Anfahrt.",
        budgetCount,
        false,
        `<div class="space-y-4">
          ${renderSelect(`${prefix}-cost`, "Preisniveau", "cost", Object.entries(costLevelLabels).map(([value, label]) => ({ value, label })), state.maxCostLevel)}
          ${renderSelect(`${prefix}-price`, "Maximaler Demo-Preis", "price", priceOptions, state.maxPriceSek?.toString() ?? "")}
          ${renderSelect(`${prefix}-duration`, "Zeit vor Ort", "duration", durationOptions, state.maxDurationMinutes?.toString() ?? "")}
          ${renderSelect(`${prefix}-travel`, "Anfahrt ab Hauptbahnhof", "travel", travelTimeOptions, state.maxTravelTimeMinutes?.toString() ?? "")}
        </div>`,
      )}

      ${renderFilterAccordion(
        "weather",
        "Wetter & Tageszeit",
        "Innenanteil, Regen, Saison und beste Tageszeit.",
        weatherCount,
        false,
        `<div class="space-y-5">
          <div>
            <div class="flex items-center justify-between gap-3">
              <label for="${prefix}-indoor" class="text-xs font-bold text-slate-700">Mindestens drinnen</label>
              <output data-output="indoor" for="${prefix}-indoor" class="rounded-full bg-white px-2.5 py-1 text-xs font-black text-teal-800 shadow-sm">${state.minIndoorPercentage === 0 ? "egal" : `${state.minIndoorPercentage}%`}</output>
            </div>
            <input id="${prefix}-indoor" data-filter="indoor" type="range" min="0" max="100" step="25" value="${state.minIndoorPercentage}" class="range-input mt-3 w-full" />
          </div>
          ${renderToggle(`${prefix}-rain-safe`, "rain-safe", "Explizit regensicher", "Nur Einträge mit rainSafe = true.", state.rainSafeOnly)}
          ${renderChoiceGroup("Saison", "Mehrere Saisons werden als Oder-Auswahl behandelt.", `${prefix}-season`, "season", seasonDefinitions, state.seasons)}
          ${renderChoiceGroup("Beste Tageszeit", "Mindestens eine gewählte Tageszeit muss passen.", `${prefix}-time`, "time", timeOfDayDefinitions, state.timesOfDay)}
        </div>`,
      )}

      ${renderFilterAccordion(
        "planning",
        "Planung & Zugänglichkeit",
        "Buchung, Kinder, Mobilität und Aufwand.",
        planningCount,
        false,
        `<div class="space-y-4">
          ${renderSelect(`${prefix}-booking`, "Buchungsbedarf", "booking", bookingPolicyOptions, state.bookingPolicy)}
          ${renderSelect(`${prefix}-mobility`, "Mobilitätsanforderung", "mobility", mobilityLimitOptions, state.maxMobility)}
          ${renderToggle(`${prefix}-children`, "children", "Explizit für Kinder geeignet", "Nur Einträge mit positiver Kinder-Eignung.", state.childrenOnly)}
          ${renderNumberInput(`${prefix}-child-age`, "Alter des Kindes", "child-age", state.childAge, 0, 17, "Optional: prüft minAge und maxAge.")}
          ${renderSelect(`${prefix}-effort`, "Maximaler körperlicher Aufwand", "effort", [
            { value: "", label: "Beliebiger Aufwand" },
            { value: "1", label: "Maximal 1/5" },
            { value: "2", label: "Maximal 2/5" },
            { value: "3", label: "Maximal 3/5" },
            { value: "4", label: "Maximal 4/5" },
            { value: "5", label: "Bis 5/5" },
          ], state.maxPhysicalEffort?.toString() ?? "")}
          <div>
            <div class="flex items-center justify-between gap-3">
              <label for="${prefix}-stroller" class="text-xs font-bold text-slate-700">Kinderwagen-Eignung</label>
              <output data-output="stroller" for="${prefix}-stroller" class="rounded-full bg-white px-2.5 py-1 text-xs font-black text-teal-800 shadow-sm">${state.minStrollerScore === 0 ? "egal" : `ab ${state.minStrollerScore}`}</output>
            </div>
            <input id="${prefix}-stroller" data-filter="stroller" type="range" min="0" max="5" step="1" value="${state.minStrollerScore}" class="range-input mt-3 w-full" />
          </div>
          ${renderToggle(`${prefix}-car-free`, "car-free", "Ohne Auto machbar", "Auto darf laut Datenmodell nicht erforderlich sein.", state.carFreeOnly)}
          ${renderToggle(`${prefix}-quiet`, "quiet", "Ruhigere Zeiten möglich", "Nur Erlebnisse mit quietTimeAvailable = true.", state.quietOnly)}
          ${renderToggle(`${prefix}-toilets`, "toilets", "Toilette vorhanden", "Praktisch für Familien und längere Besuche.", state.toiletsOnly)}
        </div>`,
      )}

      ${renderFilterAccordion(
        "place",
        "Ort & Fortbewegung",
        "Transport, Viertelcharakter und Art des Wasserbezugs.",
        placeCount,
        false,
        `<div class="space-y-5">
          ${renderChoiceGroup("Transport", "Eine der gewählten Optionen muss möglich sein.", `${prefix}-transport`, "transport", transportModeDefinitions, state.transportModes)}
          ${renderChoiceGroup("Viertelcharakter", "Eine ausgewählte Stimmung muss im Datensatz vorkommen.", `${prefix}-character`, "character", neighbourhoodCharacterDefinitions, state.neighbourhoodCharacters)}
          ${renderChoiceGroup("Wasserbezug", "Ufer, Hafen, Insel, Fähre und weitere Beziehungen.", `${prefix}-water`, "water", waterRelationshipDefinitions, state.waterRelationships)}
        </div>`,
      )}
    </div>
  `;
}

function renderFilterAccordion(
  section: string,
  title: string,
  description: string,
  count: number,
  open: boolean,
  content: string,
): string {
  return `
    <details class="filter-accordion group rounded-2xl border border-slate-200 bg-white" ${open ? "open" : ""}>
      <summary class="flex cursor-pointer list-none items-center justify-between gap-3 rounded-2xl px-3.5 py-3 transition hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700">
        <span class="text-sm font-black text-slate-950">${escapeHtml(title)}<span class="sr-only">. ${escapeHtml(description)}</span></span>
        <span class="flex shrink-0 items-center gap-2">
          <span data-section-count="${escapeAttribute(section)}" class="rounded-full bg-coral-100 px-2 py-1 text-[0.65rem] font-black text-coral-800 ${count === 0 ? "hidden" : ""}">${count}</span>
          <span class="text-slate-400 transition group-open:rotate-180">${icon("chevronDown", "size-4")}</span>
        </span>
      </summary>
      <div class="border-t border-slate-100 px-3.5 pb-3.5 pt-3">${content}</div>
    </details>
  `;
}

function renderDimensionControl(
  definition: (typeof coreDimensionDefinitions)[number],
  state: FilterState,
  prefix: string,
): string {
  const value = state.minScores[definition.key];
  const id = `${prefix}-score-${definition.key}`;
  const expandableHelp = definition.key === "experienceDensity";
  return `
    <div class="group">
      <div class="flex items-center justify-between gap-3">
        <span class="flex min-w-0 items-center gap-1.5">
          <label for="${id}" class="min-w-0 text-xs font-bold text-slate-700" title="${escapeAttribute(definition.description)}">${escapeHtml(definition.label)}</label>
          ${expandableHelp ? `
            <details class="dimension-help relative shrink-0">
              <summary class="grid size-6 cursor-pointer list-none place-items-center rounded-full text-amber-700 transition hover:bg-amber-50 hover:text-amber-900 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-amber-500" aria-label="Erklärung zu ${escapeAttribute(definition.label)}">${icon("hourglass", "size-3.5")}</summary>
              <p class="absolute left-0 top-full z-30 mt-2 w-56 max-w-[calc(100vw-4rem)] rounded-xl border border-amber-200 bg-amber-50 p-3 text-[0.72rem] font-medium leading-5 text-slate-700 shadow-xl">${escapeHtml(definition.description)}</p>
            </details>
          ` : ""}
        </span>
        <output data-score-output="${definition.key}" for="${id}" class="shrink-0 rounded-full bg-sand-50 px-2.5 py-1 text-xs font-black ${value > 0 ? "text-teal-800 shadow-sm" : "text-slate-400"}">${value === 0 ? "egal" : `ab ${value}`}</output>
      </div>
      <input id="${id}" data-filter="score" data-score-key="${definition.key}" type="range" min="0" max="5" step="1" value="${value}" class="range-input mt-2.5 w-full" aria-describedby="${id}-help" />
      <p id="${id}-help" class="sr-only">${escapeHtml(definition.description)}</p>
    </div>
  `;
}

function renderSelect(
  id: string,
  label: string,
  filter: string,
  options: Array<{ value: string; label: string }>,
  selected: string,
): string {
  return `
    <label for="${id}" class="block">
      <span class="mb-1.5 block text-xs font-bold text-slate-700">${escapeHtml(label)}</span>
      <span class="relative block">
        <select id="${id}" data-filter="${filter}" class="w-full appearance-none rounded-xl border border-slate-300 bg-white py-2.5 pl-3 pr-9 text-sm font-semibold text-slate-800 outline-none transition focus:border-teal-600 focus:ring-4 focus:ring-teal-100">
          ${options.map((option) => `<option value="${escapeAttribute(option.value)}" ${option.value === selected ? "selected" : ""}>${escapeHtml(option.label)}</option>`).join("")}
        </select>
        <span class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">${icon("chevronDown", "size-4")}</span>
      </span>
    </label>
  `;
}

function renderNumberInput(
  id: string,
  label: string,
  filter: string,
  value: number | null,
  min: number,
  max: number,
  help: string,
): string {
  return `
    <label for="${id}" class="block">
      <span class="mb-1.5 block text-xs font-bold text-slate-700">${escapeHtml(label)}</span>
      <input id="${id}" data-filter="${filter}" type="number" inputmode="numeric" min="${min}" max="${max}" value="${value ?? ""}" placeholder="z. B. 8" class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-teal-600 focus:ring-4 focus:ring-teal-100" />
      <span class="sr-only">${escapeHtml(help)}</span>
    </label>
  `;
}

function renderToggle(
  id: string,
  filter: string,
  label: string,
  description: string,
  checked: boolean,
): string {
  return `
    <label for="${id}" class="flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-3 transition hover:border-teal-300" title="${escapeAttribute(description)}">
      <span class="text-xs font-black text-slate-800">${escapeHtml(label)}<span class="sr-only">. ${escapeHtml(description)}</span></span>
      <span class="relative mt-0.5 shrink-0">
        <input id="${id}" data-filter="${filter}" type="checkbox" class="peer sr-only" ${checked ? "checked" : ""} />
        <span class="block h-6 w-11 rounded-full bg-slate-300 transition peer-checked:bg-teal-800 peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-teal-700"></span>
        <span class="absolute left-1 top-1 size-4 rounded-full bg-white shadow transition peer-checked:translate-x-5"></span>
      </span>
    </label>
  `;
}

function renderChoiceGroup<T extends string>(
  label: string,
  help: string,
  idPrefix: string,
  filter: string,
  definitions: ChoiceDefinition<T>[],
  selected: T[],
): string {
  return `
    <fieldset>
      <legend class="text-xs font-bold text-slate-700">${escapeHtml(label)}</legend>
      <p class="sr-only">${escapeHtml(help)}</p>
      <div class="mt-2.5 flex flex-wrap gap-2">
        ${renderChoiceChips(idPrefix, filter, definitions, selected)}
      </div>
    </fieldset>
  `;
}

function renderChoiceChips<T extends string>(
  idPrefix: string,
  filter: string,
  definitions: Array<ChoiceDefinition<T> | { value: T; label: string }>,
  selected: T[],
): string {
  return definitions.map((definition) => {
    const checked = selected.includes(definition.value);
    const id = `${idPrefix}-${definition.value}`;
    return `
      <label for="${id}" class="cursor-pointer">
        <input id="${id}" class="peer sr-only" type="checkbox" data-filter="${filter}" value="${definition.value}" ${checked ? "checked" : ""} />
        <span class="inline-flex rounded-full border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-slate-600 transition hover:border-teal-400 peer-checked:border-teal-800 peer-checked:bg-teal-950 peer-checked:text-white peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-teal-700">${escapeHtml(definition.label)}</span>
      </label>
    `;
  }).join("");
}

export function renderResults(
  state: FilterState,
  ranked: RankedAttraction[],
  totalAttractions: number,
  chips: FilterChip[],
  relaxSuggestions: RelaxSuggestion[],
): string {
  const countLabel = ranked.length === 1 ? "1 Erlebnis" : `${ranked.length} Erlebnisse`;
  const filtered = chips.length > 0;

  return `
    <div class="flex min-w-0 items-center justify-between gap-2">
      <h2 id="results-heading" tabindex="-1" class="min-w-0 truncate text-lg font-black tracking-tight text-slate-950 sm:text-2xl" aria-live="polite" title="${ranked.length} von ${totalAttractions} Erlebnissen">${countLabel}<span class="sr-only"> von ${totalAttractions}</span></h2>
      <div class="flex shrink-0 items-center gap-1.5">
        <button type="button" data-action="open-mobile-filters" class="relative grid size-10 place-items-center rounded-full border border-slate-300 bg-white text-teal-950 shadow-sm transition hover:border-teal-500 hover:bg-teal-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700 lg:hidden" aria-label="Filter öffnen, ${chips.length} aktiv">
          ${icon("filter", "size-[1.125rem]")}
          <span data-filter-count-badge class="absolute -right-0.5 -top-0.5 grid min-w-4 place-items-center rounded-full bg-coral-600 px-1 text-[0.6rem] font-black leading-4 text-white ${chips.length === 0 ? "hidden" : ""}">${chips.length}</span>
        </button>
        <label class="relative block">
          <span class="sr-only">Sortierung</span>
          <span class="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-slate-400">${icon("arrowUpDown", "size-3.5")}</span>
          <select data-filter="sort" class="h-10 w-28 appearance-none rounded-full border border-slate-300 bg-white py-0 pl-7 pr-6 text-xs font-bold text-slate-800 shadow-sm outline-none transition focus:border-teal-600 focus:ring-4 focus:ring-teal-100 sm:w-44">
            <option value="match" ${state.sortBy === "match" ? "selected" : ""}>Match</option>
            <option value="overall" ${state.sortBy === "overall" ? "selected" : ""}>Bewertung</option>
            <option value="value" ${state.sortBy === "value" ? "selected" : ""}>Preis-Leistung</option>
            <option value="duration" ${state.sortBy === "duration" ? "selected" : ""}>Dauer</option>
            <option value="price" ${state.sortBy === "price" ? "selected" : ""}>Preis</option>
            <option value="travel" ${state.sortBy === "travel" ? "selected" : ""}>Anfahrt</option>
          </select>
          <span class="pointer-events-none absolute inset-y-0 right-2 flex items-center text-slate-400">${icon("chevronDown", "size-3.5")}</span>
        </label>
        ${filtered ? `
          <button type="button" data-action="reset-filters" class="grid size-10 place-items-center rounded-full border border-slate-300 bg-white text-slate-600 shadow-sm transition hover:border-coral-300 hover:bg-coral-50 hover:text-coral-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral-600" aria-label="Alle Filter zurücksetzen" title="Alle Filter zurücksetzen">${icon("reset", "size-4")}</button>
        ` : ""}
      </div>
    </div>

    ${filtered ? `<div class="mt-2" data-active-filters>${renderActiveChips(chips)}</div>` : ""}

    ${ranked.length ? `<div class="mt-3 grid gap-4 xl:grid-cols-2">${ranked.map(renderAttractionCard).join("")}</div>` : renderEmptyState(relaxSuggestions)}
  `;
}

function renderActiveChips(chips: FilterChip[]): string {
  return `
    <div class="active-filter-strip flex gap-1.5 overflow-x-auto pb-1" aria-label="Aktive Filter">
      ${chips.map((chip) => `
        <button type="button" data-remove-filter="${escapeAttribute(chip.id)}" class="group inline-flex shrink-0 items-center gap-1.5 rounded-full border border-teal-200 bg-teal-50 px-2.5 py-1.5 text-[0.7rem] font-bold text-teal-900 transition hover:border-coral-300 hover:bg-coral-50 hover:text-coral-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700">
          ${escapeHtml(chip.label)}
          <span class="text-teal-500 transition group-hover:text-coral-700">${icon("close", "size-3.5")}</span>
        </button>
      `).join("")}
    </div>
  `;
}

function renderAttractionCard(item: RankedAttraction): string {
  const { attraction, matchScore, reasons } = item;
  const visual = getAttractionVisual(attraction);
  const duration = attraction.time?.recommendedDurationMinutes ?? attraction.time?.typicalDurationMinutes;
  const travelTime = attraction.time?.travelTimeFromCentralStationMinutes;
  const price = getComparablePrice(attraction);
  const neighbourhood = attraction.destination.neighbourhood ?? attraction.destination.city;
  const topScores = reasons.slice(0, 3);

  return `
    <article class="group flex min-h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-0.5 hover:border-teal-200 hover:shadow-xl hover:shadow-teal-950/10">
      <div class="relative h-44 overflow-hidden bg-slate-100 sm:h-52">
        <img src="${escapeAttribute(visual.image)}" alt="${escapeAttribute(visual.alt)}" loading="lazy" decoding="async" data-attraction-image data-image-fallback="${escapeAttribute(fallbackImage)}" class="h-full w-full object-cover ${visual.objectPositionClass} transition duration-500 group-hover:scale-[1.03]" />
        <div class="absolute inset-0 bg-gradient-to-t ${visual.accentClass}"></div>
        <div class="absolute inset-x-3 top-3 flex items-start justify-between gap-3 sm:inset-x-4 sm:top-4">
          <span class="max-w-[70%] truncate rounded-full border border-white/40 bg-white/90 px-2.5 py-1 text-[0.62rem] font-black uppercase tracking-[0.1em] text-slate-800 shadow-sm backdrop-blur">${escapeHtml(visual.label)}</span>
          <span class="grid size-12 place-items-center rounded-full border border-white/30 bg-teal-950/90 text-center text-white shadow-lg backdrop-blur" aria-label="${matchScore} Prozent Übereinstimmung">
            <span><span class="block text-base font-black leading-none">${matchScore}</span><span class="block text-[0.48rem] font-bold uppercase tracking-wider text-teal-100">Match</span></span>
          </span>
        </div>
        <div class="absolute inset-x-4 bottom-3.5 text-white sm:inset-x-5 sm:bottom-4">
          <div class="flex items-center gap-1.5 text-xs font-semibold text-white/85">${icon("mapPin", "size-3.5")} ${escapeHtml(neighbourhood)}</div>
          <h3 class="mt-1 text-xl font-black tracking-tight sm:text-2xl">${escapeHtml(attraction.title)}</h3>
        </div>
      </div>

      <div class="flex flex-1 flex-col p-4 sm:p-5">
        <p class="line-clamp-2 text-sm leading-6 text-slate-600">${escapeHtml(attraction.editorial?.summary ?? attraction.editorial?.dek ?? "")}</p>

        <div class="relative mt-3 grid grid-cols-3 gap-2 rounded-xl bg-sand-50 p-2.5 text-center">
          ${renderPracticalMetric(icon("hourglass", "size-4"), duration ? formatMinutes(duration) : "offen", "Dauer", "Empfohlene Aufenthaltsdauer laut Datensatz.")}
          ${renderPracticalMetric(icon("wallet", "size-4"), formatSek(price), "Demo-Preis")}
          ${renderPracticalMetric(icon("train", "size-4"), travelTime !== undefined ? `${travelTime} Min.` : "offen", "Anfahrt")}
        </div>

        <div class="mt-3 flex flex-wrap gap-1.5" aria-label="Stärken">
          ${topScores.map(renderReasonChip).join("")}
        </div>

        <details class="card-disclosure group/disclosure mt-3 rounded-xl border border-coral-100 bg-coral-50/60">
          <summary class="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2.5 text-left text-xs font-black text-coral-800 transition hover:bg-coral-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral-500">
            <span class="inline-flex items-center gap-2">${icon("info", "size-3.5")} Ehrliches Fazit</span>
            <span class="text-coral-500 transition group-open/disclosure:rotate-180">${icon("chevronDown", "size-3.5")}</span>
          </summary>
          <p class="border-t border-coral-100 px-3 py-3 text-xs leading-5 text-slate-700">${escapeHtml(attraction.editorial?.honestVerdict ?? "Details im vollständigen Eintrag prüfen.")}</p>
        </details>

        <div class="mt-auto flex items-center justify-between gap-3 pt-4">
          <div class="min-w-0 text-[0.68rem] font-bold text-slate-500">
            <span class="inline-flex items-center gap-1">${icon("ticket", "size-3.5")} ${formatBooking(attraction)}</span>
          </div>
          <button type="button" data-action="open-detail" data-attraction-id="${escapeAttribute(attraction.id)}" class="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-teal-950 px-3.5 py-2.5 text-xs font-black text-white shadow-sm transition hover:bg-coral-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700">
            Details
            ${icon("arrowRight", "size-4")}
          </button>
        </div>
      </div>
    </article>
  `;
}

function renderPracticalMetric(iconMarkup: string, value: string, label: string, help?: string): string {
  if (help) {
    return `
      <details class="metric-help group/metric relative min-w-0 border-r border-slate-200 px-1">
        <summary class="cursor-pointer list-none rounded-lg py-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500" aria-label="${escapeAttribute(`${label}: ${value}. ${help}`)}">
          <div class="mx-auto flex w-fit items-center gap-1 text-amber-700">${iconMarkup}<span class="truncate text-xs font-black text-slate-800">${escapeHtml(value)}</span></div>
          <p class="mt-1 text-[0.58rem] font-semibold uppercase tracking-wide text-slate-400">${escapeHtml(label)}</p>
        </summary>
        <p class="absolute left-0 top-full z-30 mt-2 w-44 rounded-xl border border-amber-200 bg-amber-50 p-3 text-left text-[0.7rem] font-medium leading-5 text-slate-700 shadow-xl">${escapeHtml(help)}</p>
      </details>
    `;
  }

  return `
    <div class="min-w-0 border-r border-slate-200 px-1 last:border-r-0">
      <div class="mx-auto flex w-fit items-center gap-1 text-teal-800">${iconMarkup}<span class="truncate text-xs font-black text-slate-800">${escapeHtml(value)}</span></div>
      <p class="mt-1 text-[0.58rem] font-semibold uppercase tracking-wide text-slate-400">${escapeHtml(label)}</p>
    </div>
  `;
}

function renderReasonChip(reason: MatchReason): string {
  const value = reason.score !== undefined
    ? escapeHtml(`${reason.score}/5`)
    : icon("check", "size-3");
  return `<span title="${escapeAttribute(reason.explanation ?? reason.label)}" class="inline-flex items-center gap-1 rounded-full border border-teal-100 bg-teal-50 px-2 py-1 text-[0.66rem] font-bold text-teal-900"><span class="max-w-28 truncate">${escapeHtml(reason.label)}</span><span class="text-teal-700">${value}</span></span>`;
}

function renderReasonBar(reason: MatchReason): string {
  const score = reason.score ?? 5;
  return `
    <div title="${escapeAttribute(reason.explanation ?? reason.label)}">
      <div class="mb-1.5 flex items-center justify-between gap-3 text-xs">
        <span class="truncate font-bold text-slate-700">${escapeHtml(reason.label)}</span>
        ${reason.score ? `<span class="shrink-0 font-black text-teal-800">${reason.score}/5</span>` : `<span class="shrink-0 text-teal-700">${icon("check", "size-4")}</span>`}
      </div>
      <div class="h-1.5 overflow-hidden rounded-full bg-slate-100"><div class="h-full rounded-full bg-gradient-to-r from-teal-700 to-cyan-500" style="width: ${Math.max(12, score * 20)}%"></div></div>
    </div>
  `;
}

function renderEmptyState(suggestions: RelaxSuggestion[]): string {
  return `
    <div class="mt-7 rounded-3xl border border-dashed border-slate-300 bg-sand-50 px-6 py-14 text-center">
      <span class="mx-auto grid size-14 place-items-center rounded-2xl bg-white text-teal-800 shadow-sm">${icon("filter", "size-7")}</span>
      <h3 class="mt-5 text-xl font-black text-slate-950">Diese Kombination ist im Demo-Datensatz zu eng.</h3>
      <p class="mx-auto mt-2 max-w-lg text-sm leading-6 text-slate-600">Lockere gezielt einen Filter. Die Vorschläge zeigen, welche einzelne Änderung wieder Ergebnisse liefert.</p>
      ${suggestions.length ? `
        <div class="mx-auto mt-6 flex max-w-xl flex-col gap-2">
          ${suggestions.map((suggestion) => `
            <button type="button" data-remove-filter="${escapeAttribute(suggestion.chipId)}" class="flex items-center justify-between gap-4 rounded-2xl border border-teal-200 bg-white px-4 py-3 text-left text-sm font-bold text-teal-950 transition hover:border-teal-500 hover:bg-teal-50">
              <span>„${escapeHtml(suggestion.label)}“ entfernen</span>
              <span class="shrink-0 rounded-full bg-teal-950 px-2.5 py-1 text-xs text-white">${suggestion.resultCount} ${suggestion.resultCount === 1 ? "Treffer" : "Treffer"}</span>
            </button>
          `).join("")}
        </div>
      ` : ""}
      <button type="button" data-action="reset-filters" class="mt-6 inline-flex items-center gap-2 rounded-full bg-teal-950 px-5 py-3 text-sm font-black text-white transition hover:bg-teal-900">Alle Filter zurücksetzen</button>
    </div>
  `;
}

function renderMobileFilterDialog(state: FilterState, chipCount: number): string {
  return `
    <dialog id="filter-dialog" class="mobile-filter-dialog bg-white p-0 shadow-2xl backdrop:bg-teal-950/60 backdrop:backdrop-blur-sm lg:hidden">
      <div class="flex h-full flex-col">
        <div class="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <p class="text-xs font-bold uppercase tracking-[0.15em] text-coral-600">Fein abstimmen</p>
            <h2 class="text-xl font-black text-slate-950">Deine Filter <span data-mobile-filter-count class="text-sm text-slate-500">(${chipCount})</span></h2>
          </div>
          <button type="button" data-action="close-mobile-filters" class="grid size-10 place-items-center rounded-full bg-slate-100 text-slate-700 transition hover:bg-slate-200" aria-label="Filter schließen">${icon("close", "size-5")}</button>
        </div>
        <div class="flex-1 overflow-y-auto bg-sand-50/70 p-4">
          ${renderFilterPanel(state, "mobile")}
        </div>
        <div class="border-t border-slate-200 bg-white p-4 shadow-[0_-10px_30px_rgba(15,23,42,0.08)]">
          <button type="button" data-action="apply-mobile-filters" class="flex w-full items-center justify-center gap-2 rounded-full bg-teal-950 px-5 py-3.5 text-sm font-black text-white transition hover:bg-teal-900">Ergebnisse anzeigen ${icon("arrowRight", "size-4")}</button>
        </div>
      </div>
    </dialog>
  `;
}

export function renderDetailDialog(attraction: TravelContentEntry): string {
  const visual = getAttractionVisual(attraction);
  const keyScores = [
    { label: "Sonne", score: getDimensionScore(attraction, "sunny") },
    { label: "Atmosphäre", score: getDimensionScore(attraction, "atmosphere") },
    { label: "Regen", score: getDimensionScore(attraction, "rain") },
    { label: "Geschichte", score: getDimensionScore(attraction, "history") },
    { label: "Zu Fuß", score: getDimensionScore(attraction, "walking") },
    { label: "Wasser", score: getDimensionScore(attraction, "water") },
    { label: "Lokal", score: getDimensionScore(attraction, "localness") },
    { label: "Preis-Leistung", score: getDimensionScore(attraction, "value") },
    { label: "Zeiteffizienz", score: getDimensionScore(attraction, "timeEfficiency") },
  ].filter((item) => item.score > 0);

  const seasons = attraction.tripContext?.season?.map((value) => seasonDefinitions.find((definition) => definition.value === value)?.label ?? value).join(", ");
  const times = attraction.time?.bestTimeOfDay?.map((value) => timeOfDayDefinitions.find((definition) => definition.value === value)?.label ?? value).join(", ");
  const modes = attraction.experienceProfile.transport?.modes?.map((value) => transportModeDefinitions.find((definition) => definition.value === value)?.label ?? value).join(", ");

  return `
    <article>
      <div class="relative h-64 overflow-hidden bg-slate-100 sm:h-80">
        <img src="${escapeAttribute(visual.image)}" alt="${escapeAttribute(visual.alt)}" data-attraction-image data-image-fallback="${escapeAttribute(fallbackImage)}" class="h-full w-full object-cover ${visual.objectPositionClass}" />
        <div class="absolute inset-0 bg-gradient-to-t ${visual.accentClass}"></div>
        <button type="button" data-action="close-detail" class="absolute right-4 top-4 grid size-10 place-items-center rounded-full border border-white/30 bg-slate-950/60 text-white backdrop-blur transition hover:bg-slate-950" aria-label="Details schließen">${icon("close", "size-5")}</button>
        <div class="absolute inset-x-6 bottom-6 text-white">
          <p class="text-xs font-black uppercase tracking-[0.15em] text-white/80">${escapeHtml(visual.label)}</p>
          <h2 class="mt-2 text-3xl font-black tracking-tight sm:text-4xl">${escapeHtml(attraction.title)}</h2>
          <p class="mt-2 flex items-center gap-1.5 text-sm font-semibold text-white/85">${icon("mapPin", "size-4")} ${escapeHtml(attraction.destination.neighbourhood ?? attraction.destination.city)}</p>
        </div>
      </div>
      <div class="p-6 sm:p-8">
        <div class="grid gap-8 lg:grid-cols-[minmax(0,1fr)_19rem]">
          <div>
            <p class="text-lg leading-8 text-slate-700">${escapeHtml(attraction.editorial?.summary ?? "")}</p>
            <div class="mt-7 rounded-2xl bg-teal-950 p-5 text-white">
              <p class="text-xs font-black uppercase tracking-[0.15em] text-coral-300">Lohnt sich das für mich?</p>
              <p class="mt-2 text-base leading-7 text-teal-50">${escapeHtml(attraction.editorial?.decision ?? attraction.editorial?.honestVerdict ?? "")}</p>
            </div>

            <div class="mt-8 grid gap-5 sm:grid-cols-2">
              ${renderEditorialList("Dafür spricht", attraction.editorial?.pros ?? [], "positive")}
              ${renderEditorialList("Das solltest du wissen", attraction.editorial?.cons ?? [], "caution")}
              ${renderEditorialList("Besonders passend für", attraction.editorial?.bestFor ?? [], "neutral")}
              ${renderEditorialList("Eher nicht ideal für", attraction.editorial?.notIdealFor ?? [], "neutral")}
            </div>

            ${attraction.editorial?.counterfactualRecommendation?.reason ? `<div class="mt-7 rounded-2xl border border-amber-200 bg-amber-50 p-5"><p class="text-xs font-black uppercase tracking-[0.15em] text-amber-800">Wann lieber auslassen?</p><p class="mt-2 text-sm leading-6 text-slate-700">${escapeHtml(attraction.editorial.counterfactualRecommendation.reason)}</p></div>` : ""}

            ${(attraction.content.sections ?? []).length ? `
              <div class="mt-8 space-y-5 border-t border-slate-200 pt-7">
                ${(attraction.content.sections ?? []).map((section) => `<section><h3 class="text-base font-black text-slate-950">${escapeHtml(section.heading ?? "")}</h3><p class="mt-2 text-sm leading-6 text-slate-600">${escapeHtml(section.body ?? "")}</p></section>`).join("")}
              </div>
            ` : ""}
          </div>
          <aside>
            <div class="rounded-2xl border border-slate-200 bg-sand-50 p-5">
              <h3 class="text-sm font-black text-slate-950">Erlebnisprofil</h3>
              <div class="mt-4 space-y-4">
                ${keyScores.map((item) => renderReasonBar({ key: item.label, label: item.label, score: item.score })).join("")}
              </div>
            </div>
            <div class="mt-5 rounded-2xl border border-slate-200 p-5">
              <h3 class="text-sm font-black text-slate-950">Praktisch</h3>
              <dl class="mt-4 space-y-3 text-sm">
                ${renderDefinition("Dauer", attraction.time?.recommendedDurationMinutes ? formatMinutes(attraction.time.recommendedDurationMinutes) : "Nicht gepflegt")}
                ${renderDefinition("Demo-Preis", formatSek(getComparablePrice(attraction)))}
                ${renderDefinition("Preisniveau", formatCostLevel(attraction.cost?.costLevel))}
                ${renderDefinition("Vorbuchung", formatBooking(attraction))}
                ${renderDefinition("Innenanteil", attraction.weather?.indoorPercentage !== undefined ? `${attraction.weather.indoorPercentage}%` : "Nicht gepflegt")}
                ${renderDefinition("Saison", seasons || "Nicht gepflegt")}
                ${renderDefinition("Tageszeit", times || "Nicht gepflegt")}
                ${renderDefinition("Transport", modes || "Nicht gepflegt")}
              </dl>
            </div>
            <div class="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-xs leading-5 text-slate-600">
              <p class="font-black text-slate-800">Datenstatus: Entwurf</p>
              <p class="mt-1">Preise und Praxisangaben sind bewusst Demo-Werte und müssen vor Veröffentlichung fachlich geprüft werden.</p>
            </div>
          </aside>
        </div>
      </div>
    </article>
  `;
}

function renderEditorialList(title: string, items: string[], tone: "positive" | "caution" | "neutral"): string {
  if (items.length === 0) return "";
  const toneClass = tone === "positive" ? "text-emerald-700" : tone === "caution" ? "text-coral-700" : "text-teal-700";
  return `
    <section>
      <h3 class="text-sm font-black text-slate-950">${escapeHtml(title)}</h3>
      <ul class="mt-3 space-y-2 text-sm leading-6 text-slate-600">
        ${items.map((item) => `<li class="flex gap-2"><span class="mt-1 ${toneClass}">${icon("check", "size-4")}</span><span>${escapeHtml(item)}</span></li>`).join("")}
      </ul>
    </section>
  `;
}

function renderDefinition(term: string, description: string): string {
  return `<div class="flex items-start justify-between gap-4"><dt class="text-slate-500">${escapeHtml(term)}</dt><dd class="max-w-44 text-right font-bold text-slate-800">${escapeHtml(description)}</dd></div>`;
}

function renderFooter(): string {
  return `
    <footer class="border-t border-slate-200 bg-white py-10">
      <div class="mx-auto flex max-w-7xl flex-col gap-5 px-4 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8">
        <div class="flex items-center gap-3">
          <span class="grid size-10 place-items-center rounded-xl bg-teal-950 text-white">${icon("compass", "size-5")}</span>
          <div><p class="text-sm font-black text-slate-950">Stockholm passt zu mir</p><p class="text-xs text-slate-500">Statischer Entscheidungs-Prototyp auf Basis des vollständigen JSON-Schemas.</p></div>
        </div>
        <p class="max-w-lg text-xs leading-5 text-slate-500 md:text-right">Alle Preis- und Praxisangaben im Demo-Datensatz müssen vor einer Veröffentlichung fachlich geprüft und aktualisiert werden.</p>
      </div>
    </footer>
  `;
}

export function formatCostLevel(level?: CostLevel): string {
  if (!level) return "offen";
  const labels: Record<CostLevel, string> = {
    free: "kostenlos",
    budget: "günstig",
    moderate: "mittel",
    expensive: "höher",
    premium: "Premium",
  };
  return labels[level];
}

function formatBooking(attraction: TravelContentEntry): string {
  const booking = attraction.logistics?.advanceBooking;
  const labels: Record<AdvanceBooking, string> = {
    never: "ohne Vorbuchung",
    recommended: "Reservierung empfohlen",
    strongly_recommended: "früh reservieren",
    essential: "Buchung nötig",
  };
  return booking ? labels[booking] : "Buchung prüfen";
}

export function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function escapeAttribute(value: string): string {
  return escapeHtml(value).replaceAll("`", "&#096;");
}
